import React from 'react';

import { tv } from '../../tv';
import { Icon } from '../icon';
import { Text } from '../text';

/**
 * The absence of content, said out loud: no vendors yet, no results for this
 * filter, nothing assigned to you.
 *
 * Flat props rather than slots, deliberately. The content model here is
 * FIXED: every empty state across the surveyed systems is a pictogram, a
 * title, a supporting line and an action, in that order, and none of the
 * parts means anything outside this arrangement. Slots earn their keep when
 * the parts vary or recombine; where they cannot, a slot API is just an
 * invitation to vary the one thing the pattern exists to keep uniform. See
 * docs/specs/frontend/design-system/naming-and-composition.md.
 *
 * About ABSENCE, not latency. There is deliberately no `isLoading`: a region
 * that is still fetching is a different state with a different owner
 * (Skeleton, when it lands), and a component that renders both is how "No
 * results" flashes at a user whose results are still on the wire.
 */

const emptyStateStyles = tv({
  slots: {
    /* Centred within whatever region is empty, and capped at a reading
     * width: an empty-state line stretched across a full-width table region
     * reads as a banner rather than a note. text-center is on the root so a
     * wrapping title centres its own lines, not just its box. */
    root: 'ck:mx-auto ck:flex ck:max-w-xs ck:flex-col ck:items-center ck:gap-2 ck:py-12 ck:text-center',
  },
});

export interface EmptyStateProps {
  /**
   * The pictogram above the title: a heroicons 24/outline component, or a
   * custom SVG drawn in currentColor. The component sets its size and colour
   * from its own recipe, so empty states cannot drift apart.
   */
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** What is absent, stated plainly: "No vendors yet". */
  title: string;
  /** Why it is absent or what would fill it, one supporting line. */
  description?: string;
  /** The way out, usually a single button or link. */
  action?: React.ReactNode;
  id?: string;
}

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  { icon, title, description, action, ...props },
  ref,
) {
  const { root } = emptyStateStyles();
  return (
    <div {...props} ref={ref} className={root()} data-component="empty-state">
      {icon ? <Icon as={icon} size="xl" color="muted" /> : null}
      <Text size="base" weight="medium">
        {title}
      </Text>
      {description ? (
        <Text size="sm" color="muted" align="center">
          {description}
        </Text>
      ) : null}
      {action}
    </div>
  );
});
